import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api.js';
import { money } from './lib/format.js';

export const SessionContext = createContext(null);
export const useSession = () => useContext(SessionContext);

const ToastContext = createContext(() => {});
export const useToast = () => useContext(ToastContext);

export function ToastHost({ children }) {
  const [message, setMessage] = useState('');
  const notify = useCallback((text) => {
    setMessage(text);
    window.setTimeout(() => setMessage((current) => (current === text ? '' : current)), 3200);
  }, []);
  return (
    <ToastContext.Provider value={notify}>
      {children}
      {message ? <div className="toast" role="status" data-testid="toast">{message}</div> : null}
    </ToastContext.Provider>
  );
}

const STATE_CLASS = {
  settled: 'settled',
  payment_pending_confirmation: 'pending',
  awaiting_payment: 'attention',
  awaiting_payment_after_return: 'attention',
  pending_balance: 'pending',
  cancelled: 'cancelled',
  pending: 'pending',
  confirmed: 'settled',
  returned: 'attention',
  terminated_by_cancellation: 'cancelled',
};

/** Amount and text always travel together; state is never conveyed by colour alone. */
export function StateChip({ state, label, overdue }) {
  return (
    <span className="row" style={{ gap: 4 }}>
      <span className={`chip ${STATE_CLASS[state] || ''}`} data-state={state}>{label}</span>
      {overdue ? <span className="chip overdue" data-testid="overdue-marker">已逾期</span> : null}
    </span>
  );
}

export function Money({ cents, testId }) {
  return <span className="amount" data-testid={testId}>{money(cents)}</span>;
}

export function Card({ title, subtitle, actions, children, testId }) {
  return (
    <section className="card" data-testid={testId}>
      {(title || actions) ? (
        <header className="card-head">
          <div>
            {title ? <h2>{title}</h2> : null}
            {subtitle ? <div className="small muted">{subtitle}</div> : null}
          </div>
          {actions ? <div className="actions">{actions}</div> : null}
        </header>
      ) : null}
      {children}
    </section>
  );
}

export function Modal({ title, subtitle, onClose, children, wide }) {
  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div className={`modal ${wide ? 'wide' : ''}`} role="dialog" aria-label={title} onMouseDown={(event) => event.stopPropagation()}>
        <header className="modal-head">
          <div>
            <h2>{title}</h2>
            {subtitle ? <div className="small muted">{subtitle}</div> : null}
          </div>
          <button type="button" className="link" onClick={onClose} aria-label="關閉">關閉</button>
        </header>
        {children}
      </div>
    </div>
  );
}

export function ErrorBanner({ error }) {
  if (!error) return null;
  const details = error.details && typeof error.details === 'object'
    ? JSON.stringify(error.details)
    : null;
  return (
    <div className="banner error" role="alert" data-testid="error-banner">
      {error.message || '操作失敗'}
      {details ? <pre className="json">{details}</pre> : null}
    </div>
  );
}

export function Loading({ label = '載入中…' }) {
  return <div className="muted small" data-testid="loading">{label}</div>;
}

/** Opens a private 付款證明 from its 證明參考 through a short-lived authorized URL. */
export function ProofReference({ reference }) {
  const [open, setOpen] = useState(false);
  const [images, setImages] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setOpen(true);
    setError(null);
    try {
      const result = await api.proofAccess(reference);
      setImages(result.images);
    } catch (failure) {
      setError(failure);
    }
  };

  return (
    <>
      <button type="button" className="link" onClick={load} data-testid={`proof-reference-${reference}`}>
        {reference}
      </button>
      {open ? (
        <Modal title={`付款證明 ${reference}`} subtitle="短效授權連結，只在 App 內開啟" onClose={() => setOpen(false)}>
          <ErrorBanner error={error} />
          {!images && !error ? <Loading /> : null}
          <div className="stack">
            {(images || []).map((image) => (
              <img key={image.proof_id} className="proof-image" src={image.url} alt={`付款證明 ${reference}`} data-testid="proof-image" />
            ))}
          </div>
          {images ? <div className="small muted">連結於 {image_expiry(images)} 過期</div> : null}
        </Modal>
      ) : null}
    </>
  );
}

function image_expiry(images) {
  const first = images[0];
  return first ? `${first.expires_at.slice(11, 19)} UTC` : '—';
}

export function ImageField({ label = '付款證明', file, onChange, testId = 'proof-file' }) {
  return (
    <label className="field">
      <span>{label}</span>
      <input
        type="file"
        accept="image/*"
        data-testid={testId}
        onChange={(event) => onChange(event.target.files && event.target.files[0] ? event.target.files[0] : null)}
      />
      {file ? <span className="small muted">已選擇：{file.name}</span> : null}
    </label>
  );
}

export function SummaryList({ rows }) {
  return (
    <div className="summary-list">
      {rows.filter(Boolean).map(([label, value]) => (
        <div key={label}>
          <span className="muted">{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  );
}

export function useAsync(loader, dependencies) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  // Only the newest load may write to state, so a slower earlier request (for
  // example an unfiltered ledger fetch) can never overwrite a newer filtered one.
  const latest = useRef(0);
  const reload = useCallback(async () => {
    latest.current += 1;
    const attempt = latest.current;
    setState((current) => ({ ...current, loading: true }));
    try {
      const data = await loader();
      if (attempt === latest.current) setState({ loading: false, data, error: null });
    } catch (error) {
      if (attempt === latest.current) setState({ loading: false, data: null, error });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
  useEffect(() => { reload(); }, [reload]);
  return useMemo(() => ({ ...state, reload }), [state, reload]);
}
